import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactPlayer from "react-player";

import useMsgFile from "@hooks/useMsgFile";
import Conversation from "./components/Conversation";
import { setNotification } from "@store/ui/uiSlice";
import { getMessages, getMessagesLastPage, createMessage } from "@store/messages/messagesAction";

import downloadSvg from "@img/download.svg";
import Loader from "@img/Loader.svg";
import { AlertBanner, Button, TextField, Card, Link } from "@runwayhealth/runway-components-react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import "./messages.scss";

const MESSAGE_FILE_KEY = "message-file";

const Messages = () => {
  const dispatch = useDispatch();
  const [messageText, setMessageText] = useState("");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [filePath, setFilePath] = useState("");
  const [base64file, setBase64file] = useState(null);
  const messages = useSelector((state) => state.messages.data);
  const cases = useSelector((state) => state.case.cases.data);
  // Cases have settled once they were fetched (or the fetch failed) at least once.
  const casesLoaded = useSelector(
    (state) => state.case.cases.success.type === "read" || !!state.case.cases.error.message
  );
  const loadingChat = useSelector((state) => state.messages.pending);
  const lastPage = useSelector((state) => state.messages.meta.last_page);
  const currentPage = useSelector((state) => state.messages.meta.current_page);
  const { uploadFile } = useMsgFile();
  // A case only gets a caseId once it has been sent to the clinic -- that is what grants
  // the patient chat privileges, not whether a patient profile exists.
  const hasClinicCase = cases.some((case_) => case_.data.caseId);
  // No case has been sent to the clinic yet -- lock the chat behind the
  // "complete your consultation" banner instead of fetching (and spinning forever).
  // Cases may still be loading (e.g. in a fresh tab), so only lock once they have settled.
  const lockedChat = casesLoaded && !hasClinicCase;

  useEffect(() => {
    dispatch(setNotification({ message: false }));
  }, []);

  useEffect(() => {
    if (hasClinicCase) {
      // Initialize messenger.
      dispatch(getMessagesLastPage());
    }
  }, [hasClinicCase]);

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (err) => {
        reject(err);
      };
    });
  };

  const onSelectFile = async (file) => {
    if (!file || file.length === 0) {
      setBase64file(undefined);
      return;
    }
    const base64img = await convertBase64(file);
    setBase64file(base64img);
  };

  const handleInfiniteScrolling = (event) => {
    if (event.target.scrollTop === 0) {
      // Set next page to fetch as the current one.
      let nextPage = currentPage + 1;
      if (currentPage <= lastPage) {
        fetchMessages(nextPage);
      }
    }
  };

  const fetchMessages = (pageNumber) => {
    if (pageNumber > 0 && hasClinicCase) {
      dispatch(
        getMessages({
          page: pageNumber,
        })
      );
    }
  };

  const createMessageFunction = async () => {
    let uploadedFile;
    const isSendingMessage = messages[messages.length - 1]?.isPending;
    if ((messageText && !/^\s*$/.test(messageText)) || (base64file && !isSendingMessage)) {
      if (file) {
        uploadedFile = await uploadFile(file, MESSAGE_FILE_KEY);
      }
      dispatch(
        createMessage({
          text: messageText,
          uploadedFile,
        })
      );
      setBase64file(null);
      setFile(null);
      setMessageText("");
    }
  };

  const saveFile = (e) => {
    const { files } = e.target;
    const file = files[0];
    setFile(file);
    onSelectFile(file);
    setFilePath(URL.createObjectURL(file));

    if (file.type.includes("image")) {
      setFilePreview(
        <div>
          <img
            className="messages-chats-file"
            src={URL.createObjectURL(file)}
            alt={file.name}
          ></img>
          <p>{file.name}</p>
        </div>
      );
    } else if (file.type.includes("video")) {
      setFilePreview(
        <ReactPlayer controls url={URL.createObjectURL(file)} width="100%" height="100%" />
      );
    } else {
      setFilePreview(<p>📑 {file.name}</p>);
    }
  };

  return (
    <>
      {!hasClinicCase && !casesLoaded ? (
        <div className="messages">
          <img className="loader-img" src={Loader} alt="Loading messages" />
        </div>
      ) : !lockedChat ? (
        <div className="messages">
          <Card className="messages-chats-main" id="messages-chats-main">
            <Conversation
              messages={messages}
              loadingChat={loadingChat}
              onScroll={handleInfiniteScrolling}
            />
            <div className="meessages-chats-footer">
              <TextField
                as="textarea"
                onChange={(e) => {
                  setMessageText(e.target.value);
                }}
                style={{ height: "auto" }}
                onKeyUp={(e) =>
                  !(e.keyCode === 13 && e.shiftKey) && e.keyCode === 13 && createMessageFunction()
                }
                value={messageText}
                placeholder="Type your message"
              ></TextField>
              <div className="messages-chats-footer-buttons">
                <label className={"download-file-message"}>
                  <img className="imgContainer" src={downloadSvg} alt="" />
                  <input type="file" onChange={saveFile} />
                </label>
                <Button
                  size="sm"
                  iconName="PaperPlaneRight"
                  isIconOnly
                  isLoading={messages[messages.length - 1]?.isPending}
                  onClick={() => createMessageFunction()}
                />
              </div>
            </div>
            {base64file && <div className="messages-chats-file">{filePreview}</div>}
          </Card>
        </div>
      ) : (
        <div className="locked-chat-message">
          <Card>
            <Container>
              <Row>
                <AlertBanner variant="info" isRounded>
                  <AlertBanner.Message>
                    Complete your consultation to chat with your physician.
                  </AlertBanner.Message>
                </AlertBanner>
                <h3>Take control of your consultations!</h3>
                <p>
                  Review pending consultations and complete checkout from your{" "}
                  <Link href="/dashboard">dashboard</Link> to unlock chat support with your
                  physician.
                </p>
              </Row>
            </Container>
          </Card>
        </div>
      )}
    </>
  );
};

export default Messages;
